import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Modal from "./Modal.tsx";
import { fetchStandardCategories } from "../services/categories.service.ts";
import type { StandardCategoryItem } from "../types/Category.ts";
import { parseApiError } from "../utils/errorHandler.ts";

const Subtitle = styled.p`
  margin: -0.75rem 0 1.25rem;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  padding-right: 2rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: min(60vh, 520px);
  overflow-y: auto;
  padding-right: 0.25rem;
  margin-right: -0.25rem;
`;

const Card = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  background: #fafafa;
  transition: background 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background: #32324a;
    border-color: #4a4a5e;
  }
`;

const CardTitle = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
  color: #001f52;
  margin-bottom: 0.75rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const Separator = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 0 0 0.75rem;
  transition: border-color 0.3s ease;

  .dark-mode & {
    border-top-color: #4a4a5e;
  }
`;

const FieldLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #001f52;
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #d0d0d0;
  }
`;

const FieldValue = styled.div`
  font-size: 0.9rem;
  color: #333;
  line-height: 1.45;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #c8c8c8;
  }
`;

const FieldBlock = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusText = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.95rem;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  color: #d32f2f;
  font-size: 0.95rem;
`;

interface StandardCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function StandardCategoriesModal({
  isOpen,
  onClose,
}: StandardCategoriesModalProps) {
  const [items, setItems] = useState<StandardCategoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLoading(true);
    setError(null);
    fetchStandardCategories()
      .then((res) => setItems(res.data))
      .catch((err) => setError(parseApiError(err)))
      .finally(() => setLoading(false));
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Alle standardkategorier"
      contentMaxWidth="640px"
    >
      <Subtitle>
        Kontakt Digital.IQ for at abonnere på disse kategorier
      </Subtitle>
      {error && <ErrorText>{error}</ErrorText>}
      {loading && <StatusText>Indlæser standardkategorier...</StatusText>}
      {!loading && !error && items.length === 0 && (
        <StatusText>Ingen standardkategorier fundet.</StatusText>
      )}
      {!loading && !error && items.length > 0 && (
        <List>
          {items.map((item) => (
            <Card key={item.id}>
              <CardTitle>{item.name}</CardTitle>
              <Separator />
              <FieldBlock>
                <FieldLabel>Beskrivelse</FieldLabel>
                <FieldValue>
                  {item.description?.trim() || "—"}
                </FieldValue>
              </FieldBlock>
              <FieldBlock>
                <FieldLabel>Antal PDF dokumenter</FieldLabel>
                <FieldValue>{item.pdfDocumentCount}</FieldValue>
              </FieldBlock>
            </Card>
          ))}
        </List>
      )}
    </Modal>
  );
}
